import { outwardCode } from "./postcode.ts";
import type { Dimensions, Issue, Parcel, ParcelItem, Pence } from "./types.ts";
import {
  gbpToPence,
  parseNumber,
  round,
  toCm,
  toKg,
  type LengthUnit,
  type WeightUnit,
} from "./units.ts";

/**
 * The only fields that ever leave the user's browser. Everything else in a
 * client file (names, address lines, emails, phone numbers, notes) is dropped
 * because it is never read: this is an allow-list, not a block-list.
 */
export const KEPT_FIELDS = [
  "orderId",
  "orderDate",
  "sku",
  "qty",
  "weight",
  "length",
  "width",
  "height",
  "carrierService",
  "cost",
  "postcode",
] as const;

export type KeptField = (typeof KEPT_FIELDS)[number];

/** Which source column feeds each kept field. Unmapped fields are left empty. */
export type ColumnMapping = Partial<Record<KeptField, string>>;

export interface UploadUnits {
  weight: WeightUnit;
  length: LengthUnit;
  /** True when the cost column includes VAT; it is then converted to ex-VAT. */
  costIncludesVat: boolean;
  /** VAT rate used for that conversion; defaults to 0.2 */
  vatRate?: number;
}

/** One cleaned line. Personal data has already been removed. */
export interface CleanRow {
  row: number;
  orderId: string;
  orderDate: string;
  sku: string | null;
  qty: number;
  weightKg: number;
  dims: Dimensions;
  carrierServiceRaw: string;
  costPence: Pence;
  outwardPostcode: string | null;
}

function text(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  return s === "" ? null : s;
}

/** Accepts YYYY-MM-DD (with optional time), DD/MM/YYYY (UK order) and Excel serial dates. */
export function parseDate(raw: unknown): string | null {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 20000 && raw < 80000) {
    const ms = Date.UTC(1899, 11, 30) + Math.round(raw) * 864e5;
    return new Date(ms).toISOString().slice(0, 10);
  }
  const s = text(raw);
  if (!s) return null;
  let y: number, m: number, d: number;
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(s);
  const uk = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:\s.*)?$/.exec(s);
  if (iso) {
    [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (uk) {
    [d, m, y] = [Number(uk[1]), Number(uk[2]), Number(uk[3])];
  } else {
    return null;
  }
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return date.toISOString().slice(0, 10);
}

/**
 * Turn one raw spreadsheet row into a CleanRow, reading only mapped kept fields.
 * Runs in the browser before upload.
 */
export function stripRow(
  raw: Record<string, unknown>,
  rowNumber: number,
  mapping: ColumnMapping,
  units: UploadUnits,
): { row: CleanRow | null; issues: Issue[] } {
  const issues: Issue[] = [];
  const get = (f: KeptField): unknown => {
    const col = mapping[f];
    return col === undefined ? undefined : raw[col];
  };
  const orderId = text(get("orderId"));
  const ref = orderId ? { orderId } : {};
  const err = (code: string, field: KeptField, message: string) =>
    issues.push({ severity: "error", code, field, row: rowNumber, ...ref, message });
  const warn = (code: string, field: KeptField, message: string) =>
    issues.push({ severity: "warning", code, field, row: rowNumber, ...ref, message });

  if (!orderId) err("missing_value", "orderId", "Order ID is blank");

  const orderDate = parseDate(get("orderDate"));
  if (!orderDate) err("bad_date", "orderDate", "Order date is blank or not a recognisable date");

  const sku = text(get("sku"));
  if (!sku) warn("missing_value", "sku", "SKU is blank; this parcel cannot be right-sized (lever 2)");

  let qty = 1;
  const qtyRaw = get("qty");
  if (text(qtyRaw) === null) {
    warn("missing_value", "qty", "Quantity is blank; assumed 1");
  } else {
    const q = parseNumber(qtyRaw);
    if (q === null || q <= 0 || !Number.isInteger(q)) err("bad_number", "qty", "Quantity must be a whole number above 0");
    else qty = q;
  }

  const num = (f: KeptField, label: string): number | null => {
    const n = parseNumber(get(f));
    if (n === null) {
      err("missing_value", f, `${label} is blank or not a number`);
      return null;
    }
    if (n <= 0) {
      err("bad_number", f, `${label} must be above 0`);
      return null;
    }
    return n;
  };
  const weight = num("weight", "Weight");
  let length: number | null, width: number | null, height: number | null;
  const oneColumn = mapping.length && mapping.length === mapping.width && mapping.length === mapping.height;
  if (oneColumn) {
    // A single "30 x 20 x 10" column
    const parts = String(get("length") ?? "")
      .split(/\s*[x×*]\s*/i)
      .map((s) => parseNumber(s));
    if (parts.length === 3 && parts.every((p) => p !== null && p > 0)) {
      [length, width, height] = parts as [number, number, number];
    } else {
      err("bad_dimensions", "length", "Dimensions must look like 30 x 20 x 10");
      length = width = height = null;
    }
  } else {
    length = num("length", "Length");
    width = num("width", "Width");
    height = num("height", "Height");
  }

  const carrierServiceRaw = text(get("carrierService"));
  if (!carrierServiceRaw) err("missing_value", "carrierService", "Carrier service is blank");

  let costPence: Pence | null = null;
  const cost = parseNumber(get("cost"));
  if (cost === null) err("missing_value", "cost", "Shipping cost is blank or not a number");
  else if (cost < 0) err("bad_number", "cost", "Shipping cost cannot be negative");
  else {
    const exVat = units.costIncludesVat ? cost / (1 + (units.vatRate ?? 0.2)) : cost;
    costPence = gbpToPence(round(exVat, 4));
  }

  let outwardPostcode: string | null = null;
  const pcRaw = text(get("postcode"));
  if (pcRaw !== null) {
    outwardPostcode = outwardCode(pcRaw);
    if (!outwardPostcode) warn("bad_postcode", "postcode", "Postcode not recognised; dropped");
  }

  if (issues.some((i) => i.severity === "error")) return { row: null, issues };

  return {
    row: {
      row: rowNumber,
      orderId: orderId!,
      orderDate: orderDate!,
      sku,
      qty,
      weightKg: toKg(weight!, units.weight),
      dims: {
        lengthCm: toCm(length!, units.length),
        widthCm: toCm(width!, units.length),
        heightCm: toCm(height!, units.length),
      },
      carrierServiceRaw: carrierServiceRaw!,
      costPence: costPence!,
      outwardPostcode,
    },
    issues,
  };
}

/** Map a client's own carrier service names (lower-cased) to our service codes. */
export type ServiceMap = Record<string, string>;

/**
 * Group cleaned lines into parcels. Data spec: one parcel per order ID and
 * carrier service, with parcel-level weight, dimensions and cost repeated on
 * each line. A group whose lines disagree is excluded and reported.
 */
export function rowsToParcels(
  rows: CleanRow[],
  serviceMap: ServiceMap,
): { parcels: Parcel[]; issues: Issue[] } {
  const issues: Issue[] = [];
  const groups = new Map<string, CleanRow[]>();
  for (const r of rows) {
    const key = `${r.orderId}\u0000${r.carrierServiceRaw.toLowerCase()}`;
    const g = groups.get(key);
    if (g) g.push(r);
    else groups.set(key, [r]);
  }

  const parcels: Parcel[] = [];
  for (const group of groups.values()) {
    const first = group[0]!;
    const same = group.every(
      (r) =>
        r.weightKg === first.weightKg &&
        r.dims.lengthCm === first.dims.lengthCm &&
        r.dims.widthCm === first.dims.widthCm &&
        r.dims.heightCm === first.dims.heightCm &&
        r.costPence === first.costPence &&
        r.orderDate === first.orderDate,
    );
    if (!same) {
      issues.push({
        severity: "error",
        code: "inconsistent_parcel",
        orderId: first.orderId,
        row: first.row,
        message: "Lines for this order disagree on weight, dimensions, cost or date; parcel excluded",
      });
      continue;
    }

    const mapped = serviceMap[first.carrierServiceRaw.toLowerCase()];
    if (!mapped) {
      issues.push({
        severity: "warning",
        code: "unknown_service",
        orderId: first.orderId,
        row: first.row,
        field: "carrierService",
        message: `Carrier service "${first.carrierServiceRaw}" is not mapped; parcel kept but not priced`,
      });
    }

    const bySku = new Map<string, number>();
    for (const r of group) {
      if (r.sku) bySku.set(r.sku, (bySku.get(r.sku) ?? 0) + r.qty);
    }
    const items: ParcelItem[] = [...bySku].map(([sku, qty]) => ({ sku, qty }));

    parcels.push({
      orderId: first.orderId,
      orderDate: first.orderDate,
      items,
      actualWeightKg: first.weightKg,
      dims: first.dims,
      carrierService: mapped ?? first.carrierServiceRaw,
      chargedPence: first.costPence,
      outwardPostcode: first.outwardPostcode,
    });
  }
  return { parcels, issues };
}
